import { cn } from "../../ui/index.js";
import type { CuePlayer, Side, SubPair } from "./cue.js";

export type PadPlayer = CuePlayer & {
  onPitch: boolean;
  sentOff: boolean;
  yellows: number;
  coach: boolean;
};

type Props = {
  side: Side;
  onSide: (side: Side) => void;
  teamNames: Record<Side, string>;
  players: PadPlayer[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** Substitution in progress: the player going off, waiting for the one coming on. */
  pendingOut: CuePlayer | null;
  pairs: SubPair[];
  onGoal: (type: "goal" | "penalty_goal" | "own_goal") => void;
  onYellow: () => void;
  onRed: () => void;
  onSub: () => void;
};

const ACT =
  "flex h-16 items-center justify-center rounded-xl text-[18px] font-black uppercase tracking-wide disabled:cursor-not-allowed disabled:opacity-35";

/** Home/away tabs, large player cards and the four actions. Picking a player
 *  then an action cues it — nothing reaches the air before EFIRGA. */
export function PlayerPad(p: Props) {
  const selected = p.players.find((x) => x.id === p.selectedId) ?? null;
  const subbing = p.pendingOut !== null;
  const usedIds = new Set(p.pairs.flatMap((x) => [x.out.id, x.in.id]));
  const canPick = (pl: PadPlayer) =>
    subbing ? !pl.coach && !pl.sentOff && !pl.onPitch && !usedIds.has(pl.id) : !pl.sentOff;
  const isPlayer = selected && !selected.coach && !selected.sentOff;
  return (
    <div className="flex min-h-0 flex-col gap-3 rounded-xl border border-border bg-surface p-3">
      <div className="flex items-center gap-2">
        {(["home", "away"] as const).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => p.onSide(s)}
            className={cn(
              "h-11 flex-1 rounded-lg text-[15px] font-bold uppercase tracking-wide",
              p.side === s
                ? "bg-ink text-canvas"
                : "bg-surface-elevated text-ink-muted hover:text-ink",
            )}
          >
            {p.teamNames[s]}
          </button>
        ))}
      </div>
      {subbing && (
        <div className="rounded-lg border border-accent-strong/50 bg-accent-strong/10 px-3 py-2 text-[14px] font-semibold text-ink">
          Chiqadi: {p.pendingOut?.lastName || p.pendingOut?.firstName} — kiruvchi o'yinchini tanlang
        </div>
      )}
      <div className="grid min-h-0 flex-1 grid-cols-2 content-start gap-2 overflow-y-auto pr-1 xl:grid-cols-3">
        {p.players.map((pl) => {
          const enabled = canPick(pl);
          return (
            <button
              key={pl.id}
              data-anchor={`player:${pl.id}`}
              type="button"
              disabled={!enabled}
              onClick={() => p.onSelect(pl.id)}
              className={cn(
                "flex h-14 items-center gap-3 rounded-lg border px-3 text-left transition-colors",
                "disabled:cursor-not-allowed disabled:opacity-30",
                p.selectedId === pl.id
                  ? "border-ready-strong bg-ready-strong/15 ring-2 ring-ready-strong"
                  : "border-border bg-surface-elevated hover:border-ink-faint",
                !pl.onPitch && !pl.coach && "border-dashed",
              )}
            >
              <span className="w-8 shrink-0 text-right font-display text-[22px] font-black tabular-nums text-ink-muted">
                {pl.coach ? "M" : (pl.number ?? "")}
              </span>
              <span
                className={cn(
                  "min-w-0 flex-1 truncate text-[16px] font-bold text-ink",
                  pl.sentOff && "line-through",
                )}
              >
                {pl.lastName || pl.firstName}
              </span>
              {pl.yellows > 0 && !pl.sentOff && (
                <span className="h-5 w-3.5 shrink-0 rounded-sm bg-card-amber" title="Sariq bor" />
              )}
            </button>
          );
        })}
      </div>
      <div className="grid grid-cols-4 gap-2">
        <button
          type="button"
          disabled={!isPlayer || subbing}
          onClick={() => p.onGoal("goal")}
          className={cn(ACT, "bg-ready-strong text-white")}
        >
          Gol <span className="ml-1.5 text-[11px] opacity-70">G</span>
        </button>
        <button
          type="button"
          disabled={!selected || selected.sentOff || subbing}
          onClick={p.onYellow}
          className={cn(ACT, "bg-card-amber text-card-amber-ink")}
        >
          {selected && selected.yellows > 0 ? "2-sariq" : "Sariq"}
          <span className="ml-1.5 text-[11px] opacity-70">Y</span>
        </button>
        <button
          type="button"
          disabled={!selected || selected.sentOff || subbing}
          onClick={p.onRed}
          className={cn(ACT, "bg-live text-white")}
        >
          Qizil <span className="ml-1.5 text-[11px] opacity-70">R</span>
        </button>
        <button
          type="button"
          disabled={!isPlayer || !selected?.onPitch || subbing}
          onClick={p.onSub}
          className={cn(ACT, "bg-accent-strong text-white")}
        >
          Almash <span className="ml-1.5 text-[11px] opacity-70">S</span>
        </button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          disabled={!isPlayer || subbing}
          onClick={() => p.onGoal("penalty_goal")}
          className="h-10 rounded-lg border border-ready-strong/60 text-[14px] font-bold text-ink disabled:opacity-35"
        >
          Penalti gol
        </button>
        <button
          type="button"
          disabled={!isPlayer || subbing}
          onClick={() => p.onGoal("own_goal")}
          className="h-10 rounded-lg border border-border text-[14px] font-bold text-ink disabled:opacity-35"
        >
          Avtogol
        </button>
      </div>
    </div>
  );
}
